import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Conversation, Message } from '../models/Conversation';
import { fetchInboxConversations, fetchConversationById, fetchConversationByUserId } from '../api/chatApi';
import { AppContext } from '../window';
import { apiClient } from '../api/client';
import { NotificationContext } from './NotificationContext';
import { mergeConversationPictures } from '../utils/mergeConversationPictures';
import { useTranslation } from '../i18n';
import { setMemberSession } from '../api/session';
import { useResync } from '../socket/resync';

export interface ConversationContextType {
  conversations: Conversation[];
  isLoading: boolean;
  hasMore: boolean;
  page: number;
  unreadMessagesInfo: Map<number, { count: number; timestamp: string }>;
  loadConversations: (pageToLoad?: number, append?: boolean) => Promise<void>;
  handleConversationClick: (conversation: Conversation, navigate: (path: string) => void, updateUser?: (updater: (prevUser: any) => any) => void) => Promise<void>;
  onConversationUpdate: (update: { type: 'new_conversation' | 'message_added'; conversation?: Conversation; conversationId?: number; message: Message }) => Promise<Conversation | null>;
  handleNewConversationNavigation: (userId: number, navigate?: (path: string) => void) => Promise<Conversation | null>;
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  resetConversations: () => void;
}

export const ConversationContext = createContext<ConversationContextType>({
  conversations: [],
  isLoading: true,
  hasMore: true,
  page: 1,
  unreadMessagesInfo: new Map(),
  loadConversations: async () => {},
  handleConversationClick: async () => {},
  onConversationUpdate: async () => null,
  handleNewConversationNavigation: async () => null,
  setConversations: () => {},
  resetConversations: () => {},
});

export const useConversationContext = () => useContext(ConversationContext);

/**
 * The inbox after a resync: the fresh first page in the server's order, the
 * local conversations without an id (just opened) before it and every other
 * loaded conversation after it, one per recipient.
 */
export const mergeInboxPage = (prev: Conversation[], firstPage: Conversation[]): Conversation[] => {
  const fresh = new Set(firstPage.map(c => c.recipient.id));
  const local = prev.filter(c => !c.id && !fresh.has(c.recipient.id));
  const rest = prev.filter(c => c.id && !fresh.has(c.recipient.id));
  return [...local, ...firstPage, ...rest];
};

interface ConversationProviderProps {
  children: React.ReactNode;
}

export const ConversationProvider: React.FC<ConversationProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  const user = appContext?.user;
  const { addNotification } = useContext(NotificationContext);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadMessagesInfo, setUnreadMessagesInfo] = useState<Map<number, { count: number; timestamp: string }>>(new Map());

  // Loads a page of conversations, appending or replacing as needed
  const loadConversations = useCallback(async (pageToLoad = 1, append = false) => {
    try {
      const { data, pageCount } = await fetchInboxConversations(pageToLoad);
      setHasMore(pageToLoad < pageCount);
      setPage(pageToLoad);
      if (append) {
        setConversations((prev) => [...prev, ...data]);
      } else {
        // When replacing, preserve any new conversations that were added
        setConversations((prev) => {
          // Find conversations without an id (new conversations)
          const newConversations = prev.filter(c => !c.id);
          // Merge new conversations with loaded data, avoiding duplicates
          const merged = [...newConversations];
          data.forEach((conv: Conversation) => {
            if (!merged.some(c => c.recipient.id === conv.recipient.id)) {
              merged.push(conv);
            }
          });
          return merged;
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleConversationClick = useCallback(async (
    conversation: Conversation, 
    navigate: (path: string) => void,
    updateUser?: (updater: (prevUser: any) => any) => void
  ) => {
    if (conversation.unreadMessages > 0) {
      // Store the unread messages info before clearing
      setUnreadMessagesInfo(prev => {
        const newMap = new Map(prev);
        newMap.set(conversation.recipient.id, {
          count: conversation.unreadMessages,
          timestamp: new Date().toISOString()
        });
        return newMap;
      });
      
      try {
  const result = await apiClient.postJson<any>('/chat/api/read-conversation', { userId: conversation.recipient.id });
  if (!result.ok) throw result.error;
        const unreadCount = conversation.unreadMessages;
        conversation.unreadMessages = 0;
        setConversations((prevConversations) =>
          prevConversations.map((c) => (c.id === conversation.id ? { ...c, unreadMessages: 0 } : c))
        );
        
        // Decrement the app context user unread message count
        if (updateUser) {
          updateUser(prevUser => 
            prevUser ? {
              ...prevUser,
              unreadMessageCount: Math.max(0, prevUser.unreadMessageCount - unreadCount)
            } : prevUser
          );
        }
      } catch (error) {
        console.error('Failed to reset unread messages:', error);
      }
    }
    navigate(`/inbox/${conversation.recipient.id}`);
  }, []);

  // Helper function to handle conversation updates with a conversation object
  const handleConversationObjectUpdate = useCallback((type: 'new_conversation' | 'message_added', conversation: Conversation, message: Message): Conversation => {
    let resultConversation = conversation;
    if (type === 'new_conversation') {
      // Fix lastMessageTime for new conversations that have invalid timestamps
      if (conversation.lastMessageTime === '1' || conversation.lastMessageTime === '0' || !conversation.lastMessageTime) {
        resultConversation = {
          ...conversation,
          lastMessageTime: new Date().toISOString()
        };
      }
      
      setConversations(prev => {
        if (!prev.some(c => c.recipient.id === conversation.recipient.id)) {
          return [resultConversation, ...prev];
        }
        return prev;
      });
    } else if (type === 'message_added') {
      setConversations(prev => {
        const existingConversationIndex = prev.findIndex(c => c.recipient.id === conversation.recipient.id);
        
        if (existingConversationIndex >= 0) {
          // Update existing conversation
          const mergedPictures = mergeConversationPictures(
            prev[existingConversationIndex].pictures,
            conversation.pictures,
            message.picture
          );

          const updatedConversation = {
            ...prev[existingConversationIndex],
            id: conversation.id || prev[existingConversationIndex].id,
            totalMessages: (prev[existingConversationIndex].totalMessages || 0) + 1,
            lastMessage: message,
            lastMessageTime: message?.time || new Date().toISOString(),
            pictures: mergedPictures ?? prev[existingConversationIndex].pictures,
          } as Conversation;

          resultConversation = updatedConversation;
          
          // Remove from current position and add to beginning
          const updatedConversations = [...prev];
          updatedConversations.splice(existingConversationIndex, 1);
          return [updatedConversation, ...updatedConversations];
        } else {
          // If conversation doesn't exist, add it (shouldn't happen but handle gracefully)
          return [conversation, ...prev];
        }
      });
    }
    
    return resultConversation;
  }, []);

  const onConversationUpdate = useCallback(async (update: { type: 'new_conversation' | 'message_added'; conversation?: Conversation; conversationId?: number; message: Message }): Promise<Conversation | null> => {
    const { type, conversation, conversationId, message } = update;
    
    if (conversation) {
      // If we already have a conversation object, use it directly
      return handleConversationObjectUpdate(type, conversation, message);
    } else if (conversationId) {
      // Fetch the conversation by ID and then handle the update
      try {
        const fetchedConversation = await fetchConversationById(conversationId);
        if (fetchedConversation) {
          return handleConversationObjectUpdate(type, fetchedConversation, message);
        }
      } catch (error) {
        console.error('Failed to fetch conversation by ID:', error);
      }
    }
    
    return null;
  }, [handleConversationObjectUpdate]);

  const handleNewConversationNavigation = useCallback(async (userId: number, navigate?: (path: string) => void): Promise<Conversation | null> => {
    // Check if we already have a conversation with this user
    const existingConversation = conversations.find(c => c.recipient.id === userId);
    if (existingConversation) {
      return existingConversation; // Return existing conversation
    }
    
    try {
      // Fetch the conversation data (may be a new conversation without an id)
      const fetchedConversation = await fetchConversationByUserId(userId);
      
      // Check for self-conversation error
      if (fetchedConversation && 'error' in fetchedConversation) {
        if (fetchedConversation.error === 'Cannot create a conversation with yourself') {
          addNotification({
            level: 'warning',
            message: t('chat.cannot_message_yourself')
          });
          // Navigate to inbox if navigate function is provided
          if (navigate) {
            navigate('/inbox');
          }
        }
        return null;
      }
      
      // Add the conversation to the list (whether it has an ID or not)
      if (fetchedConversation) {
        // Create a dummy message for the new conversation handler
        const dummyMessage: Message = {
          id: 0,
          conversationId: fetchedConversation.id || 0,
          fromUserId: 0,
          toUserId: userId,
          time: new Date().toISOString(),
          status: 0,
          type: 'text',
          typeValue: null,
          content: '',
          preparedContent: '',
          readableTime: '',
          hourMinute: '',
          dateLabel: ''
        };
        
        await onConversationUpdate({ 
          type: 'new_conversation', 
          conversation: fetchedConversation, 
          message: dummyMessage 
        });
        
        return fetchedConversation;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to handle new conversation navigation:', error);
      return null;
    }
  }, [conversations, onConversationUpdate, addNotification, t]);

  const resetConversations = useCallback(() => {
    setConversations([]);
    setPage(1);
    setHasMore(true);
    setIsLoading(false); // Set to false instead of true to avoid infinite loading
    setUnreadMessagesInfo(new Map());
  }, []);

  // Load initial conversations when the provider mounts or user changes
  useEffect(() => {
    // A 403 or a login page is a lost session only while a member is logged in
    setMemberSession(Boolean(user));
    // Only load conversations if we're not in a test environment and user is logged in
    if (typeof window !== 'undefined' && typeof fetch !== 'undefined' && user) {
      setIsLoading(true);
      loadConversations(1, false);
    } else if (!user) {
      // If no user, reset conversations
      resetConversations();
    } else {
      // In test environment, just set loading to false
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Re-run when user ID changes

  // After a connection gap re-read the first page; later pages stay as loaded
  useResync(async () => {
    if (!user) {
      return;
    }
    const { data } = await fetchInboxConversations(1);
    if (data.length > 0) {
      setConversations(prev => mergeInboxPage(prev, data));
    }
  });

  const contextValue: ConversationContextType = useMemo(() => ({
    conversations,
    isLoading,
    hasMore,
    page,
    unreadMessagesInfo,
    loadConversations,
    handleConversationClick,
    onConversationUpdate,
    handleNewConversationNavigation,
    setConversations,
    resetConversations,
  }), [
    conversations,
    isLoading,
    hasMore,
    page,
    unreadMessagesInfo,
    loadConversations,
    handleConversationClick,
    onConversationUpdate,
    handleNewConversationNavigation,
    setConversations,
    resetConversations,
  ]);

  return (
    <ConversationContext.Provider value={contextValue}>
      {children}
    </ConversationContext.Provider>
  );
};